/**
 * The two calls behind passwordless sign-in.
 *
 * Both are already deployed — see functions/src/auth/signInLink.ts.
 */

import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { call } from '../lib/callable';

interface RequestResult {
  ok: boolean;
  message: string;
}

/**
 * Ask for a link.
 *
 * The backend answers identically whether or not the address belongs to
 * anyone — same shape, same wording — so that this endpoint cannot be used to
 * ask whether a person works at a given hospital. The UI must not undo that:
 * show the message it returns, never "we don't know that address".
 */
/** `console` asks for the sysadmin console's link: its own host, a plainer email. */
export async function requestSignInLink(email: string, opts: { console?: boolean } = {}): Promise<RequestResult> {
  return call<{ email: string; console?: boolean }, RequestResult>('requestSignInLink', opts.console ? { email, console: true } : { email });
}

/**
 * Exchange a token from the emailed link for a session.
 *
 * The link is single-use and expires in 15 minutes. The backend marks it spent
 * before minting the token, so a failure here means the link is gone and the
 * user needs a new one — which is what the error text says.
 */
export async function redeemSignInLink(token: string): Promise<void> {
  const { customToken } = await call<{ token: string }, { customToken: string }>(
    'redeemSignInLink',
    { token },
  );
  await signInWithCustomToken(auth, customToken);
}
